// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { ListItem, ListItemText, Typography, Stack, Button } from '@mui/material';
import {
  AppIcon,
  PageContainer,
  SectionContainer,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { formatDate } from '@shared/ui-kit/utils';
import { type FC } from 'react';
import { EditUserProfileModal } from './edit-user-profile-modal';
import { useUserProfile } from './use-user-profile';
import { useTranslation } from 'react-i18next';

export const UserProfile: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    profile,
    isLoading,
    isUpdating,
    openEdit,
    onOpenEdit,
    onCloseEdit,
    handleSave,
    fullName,
    fullAddress,
  } = useUserProfile();

  if (isLoading || !profile) return null;

  return (
    <PageContainer>
      <div className="space-y-6" data-testid="user-profile">
        <Stack>
          <Typography variant="h1" className="text-secondary-dark">
            <Translation i18nKey="user-profile.title" ns="components" />
          </Typography>
          <Typography variant="body1" className="text-primary-medium">
            <Translation i18nKey="user-profile.subtitle" ns="components" />
          </Typography>
        </Stack>

        <SectionContainer>
          <div className="mb-2 ml-4 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <AppIcon icon="User" className="inline-block" size="xl" />
              <Typography variant="h2" className="text-secondary-dark">
                <Translation i18nKey="user-profile.personal-data" ns="components" />
              </Typography>
            </span>
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<AppIcon icon="Pen" />}
              onClick={onOpenEdit}
              data-testid="user-profile-edit-button"
            >
              <Translation i18nKey="user-profile.edit" ns="components" />
            </Button>
          </div>

          <div className="grid gap-x-6 md:grid-cols-2">
            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.full-name" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={fullName}
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Translation i18nKey="user-profile.dni" ns="components" />}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={profile.document_number}
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.email" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={profile.email}
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.phone" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={profile.phone_number}
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.address" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={fullAddress}
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.country" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={
                  profile.country
                    ? t(
                        `personal-data.countries.${profile.country.toLowerCase()}` as any,
                        { ns: 'components' },
                      )
                    : '-'
                }
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Translation i18nKey="user-profile.birth-date" ns="components" />
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
                secondary={
                  formatDate(profile.birth_date, 'standard', i18n.language) ?? ''
                }
                secondaryTypographyProps={{
                  variant: 'h5',
                  className: '!text-secondary-dark !text-lg',
                }}
              />
            </ListItem>
          </div>
        </SectionContainer>

        <EditUserProfileModal
          open={openEdit}
          initialValues={profile}
          onClose={onCloseEdit}
          onSave={handleSave}
          isUpdating={isUpdating}
        />
      </div>
    </PageContainer>
  );
};
